import { INCIDENT_CATEGORY, INCIDENT_STATUS, type IncidentStatusKey } from '@league/shared/status';
import { StatusChip } from '@league/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';
import type { Actor } from '../../lib/auth';

/**
 * INCIDENTS — one component, both sides, for the same reason the notes thread is.
 *
 * WHAT MAKES THIS DIFFERENT FROM A NOTE is that it ends with somebody saying what happened. A note
 * is told and read; an escalation stays open until it is settled, and the resolution is written
 * where the director who raised it can read it. That is the whole feature — the rest is a form.
 *
 * SETTLING IT IS HQ'S AND THE BOX IS NOT OPTIONAL. She raised it because she could not settle it,
 * so the button that closes it is not on her screen. What is on her screen is "take it back", which
 * says what it is: sometimes a thing sorts itself out before anybody at HQ reads it, and making her
 * wait for somebody else to close it teaches her not to raise the next one.
 *
 * THE OPEN ONES SIT ABOVE THE SETTLED ONES, unlike the notes inbox which is purely chronological.
 * An open escalation from last month is still somebody's problem; a resolved one from yesterday is
 * finished.
 */

interface Reply {
  id: string;
  authorId: string;
  authorName: string | null;
  authorRole: string | null;
  body: string;
  at: string;
}

interface Incident {
  id: string;
  leagueId: string;
  leagueName: string;
  sessionId: string | null;
  sessionNumber: number | null;
  directorId: string;
  directorName: string | null;
  category: keyof typeof INCIDENT_CATEGORY;
  body: string;
  status: IncidentStatusKey;
  resolution: string | null;
  resolvedAt: string | null;
  at: string;
  replies: Reply[];
}

interface League {
  id: string;
  name: string;
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function IncidentsPage({ actor }: { actor: Actor }) {
  const isCommissioner = actor.role === 'commissioner';
  const queryClient = useQueryClient();

  const [raising, setRaising] = useState(false);
  const [leagueId, setLeagueId] = useState('');
  const [category, setCategory] = useState<keyof typeof INCIDENT_CATEGORY>('dispute');
  const [body, setBody] = useState('');

  const [acting, setActing] = useState<{ id: string; kind: 'reply' | 'resolve' | 'withdraw' } | null>(
    null,
  );
  const [text, setText] = useState('');

  const list = useQuery({
    queryKey: ['incidents'],
    queryFn: () => apiFetch<{ incidents: Incident[] }>('/api/incidents'),
  });

  const leagues = useQuery({
    queryKey: ['leagues'],
    queryFn: () => apiFetch<{ leagues: League[] }>('/api/leagues'),
    enabled: !isCommissioner,
  });

  const invalidate = async () => {
    setActing(null);
    setText('');
    await queryClient.invalidateQueries({ queryKey: ['incidents'] });
    await queryClient.invalidateQueries({ queryKey: ['hq'] });
  };

  const file = useMutation({
    mutationFn: (input: { leagueId: string; category: string; body: string }) =>
      apiPost<{ incident: Incident }>('/api/incidents', input),
    onSuccess: async () => {
      setRaising(false);
      setBody('');
      await invalidate();
    },
  });

  const reply = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      apiPost<{ incident: Incident }>(`/api/incidents/${id}/replies`, { body: value }),
    onSuccess: invalidate,
  });

  const resolve = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      apiPost<{ incident: Incident }>(`/api/incidents/${id}/resolve`, { resolution: value }),
    onSuccess: invalidate,
  });

  const withdraw = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      apiPost<{ incident: Incident }>(`/api/incidents/${id}/withdraw`, { because: value }),
    onSuccess: invalidate,
  });

  if (list.isPending) return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;

  const incidents = list.data?.incidents ?? [];
  const open = incidents.filter((row) => row.status !== 'resolved');
  const error = [file.error, reply.error, resolve.error, withdraw.error].find(
    (e) => e instanceof ApiError,
  ) as ApiError | undefined;

  const myLeagues = leagues.data?.leagues ?? [];

  return (
    <div className="flex max-w-[52rem] flex-col gap-6 px-6 py-8">
      <header className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl leading-tight">
            {isCommissioner ? 'Escalations' : 'Something went wrong'}
          </h1>
          <p className="mt-1 max-w-[40rem] text-[14px] leading-relaxed text-ink-2">
            {isCommissioner
              ? 'Things directors could not settle on their own. Each one stays here until somebody says how it was handled.'
              : 'Anything you could not settle yourself — a disagreement about a score, a problem with the room, how somebody behaved. HQ picks these up and tells you what happened.'}
          </p>
        </div>
        {!isCommissioner && !raising ? (
          <Button variant="secondary" onClick={() => setRaising(true)}>
            Raise something
          </Button>
        ) : null}
      </header>

      {error ? <ErrorNote>{error.message}</ErrorNote> : null}

      {raising ? (
        <section className="flex flex-col gap-4 rounded-md border border-light bg-paper px-5 py-4">
          <label className="block">
            <span className="text-[13px] font-medium">Which league</span>
            <select
              value={leagueId}
              onChange={(event) => setLeagueId(event.target.value)}
              className="mt-2 h-11 w-full rounded-md border border-control-border bg-chalk px-3 text-[14px]"
            >
              <option value="">Choose one</option>
              {myLeagues.map((league) => (
                <option key={league.id} value={league.id}>
                  {league.name}
                </option>
              ))}
            </select>
          </label>

          {/*
            The categories carry a line of explanation each. "Conduct" on its own means whatever
            somebody assumes it means, and a director hesitating over which box to tick is a
            director who closes the form.
          */}
          <fieldset className="border-0 p-0">
            <legend className="text-[13px] font-medium">What kind of thing</legend>
            <div className="mt-2 flex flex-col gap-1.5">
              {(Object.keys(INCIDENT_CATEGORY) as (keyof typeof INCIDENT_CATEGORY)[]).map((key) => (
                <label key={key} className="flex cursor-pointer items-start gap-2.5">
                  <input
                    type="radio"
                    name="category"
                    checked={category === key}
                    onChange={() => setCategory(key)}
                    className="mt-1 accent-deep"
                  />
                  <span>
                    <span className="block text-[14px]">{INCIDENT_CATEGORY[key].label}</span>
                    <span className="block text-[12px] text-ink-3">
                      {INCIDENT_CATEGORY[key].hint}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <label className="block">
            <span className="text-[13px] font-medium">What happened</span>
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={4}
              className="mt-2 w-full rounded-md border border-stroke bg-chalk px-3 py-2 text-[14px] leading-relaxed"
            />
          </label>

          <div className="flex items-center gap-3">
            <Button
              disabled={file.isPending || !leagueId || body.trim() === ''}
              onClick={() => file.mutate({ leagueId, category, body })}
            >
              {file.isPending ? 'Sending…' : 'Send it to HQ'}
            </Button>
            <Button variant="secondary" onClick={() => setRaising(false)}>
              Cancel
            </Button>
          </div>
        </section>
      ) : null}

      {open.length > 0 && isCommissioner ? (
        <p className="rounded-md border border-rust bg-tint-rust px-5 py-3 text-[13px] text-rust">
          {open.length === 1
            ? 'One escalation is unsettled.'
            : `${open.length} escalations are unsettled.`}
        </p>
      ) : null}

      {incidents.length === 0 ? (
        <p className="rounded-md border border-dashed border-light px-5 py-10 text-center text-[14px] text-ink-3">
          {isCommissioner
            ? 'Nothing has been escalated. Directors raise things here when they cannot settle them.'
            : 'Nothing raised. Most sessions never need this.'}
        </p>
      ) : (
        <ol className="flex flex-col gap-4">
          {incidents.map((incident) => {
            const settled = incident.status === 'resolved';

            return (
              <li
                key={incident.id}
                className={[
                  'rounded-md border bg-paper',
                  settled ? 'border-light' : 'border-stroke',
                ].join(' ')}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-light px-5 py-3">
                  <div className="min-w-0">
                    <span className="text-[14px] font-medium">
                      {INCIDENT_CATEGORY[incident.category]?.label ?? incident.category}
                    </span>
                    <span className="ml-2 text-[13px] text-ink-2">
                      {incident.leagueName}
                      {incident.sessionNumber ? ` · week ${incident.sessionNumber}` : ''}
                      {isCommissioner && incident.directorName ? ` · ${incident.directorName}` : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusChip status={INCIDENT_STATUS[incident.status]} />
                    <span className="text-[12px] text-ink-3">{when(incident.at)}</span>
                  </div>
                </div>

                <div className="flex flex-col divide-y divide-light">
                  <Message
                    who={incident.directorName ?? 'A director'}
                    body={incident.body}
                    at={incident.at}
                    fromHq={false}
                  />
                  {incident.replies.map((r) => (
                    <Message
                      key={r.id}
                      who={r.authorName ?? 'Somebody'}
                      body={r.body}
                      at={r.at}
                      fromHq={r.authorRole === 'commissioner'}
                    />
                  ))}
                </div>

                {/*
                  THE RESOLUTION SITS AT THE BOTTOM ON A POSITIVE GROUND, where a thread ends. It is
                  the one thing the director came back to read, and burying it as another message in
                  the conversation would make her hunt for it.
                */}
                {settled && incident.resolution ? (
                  <div className="border-t border-light bg-tint-sage px-5 py-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sage">
                      How it was settled
                    </p>
                    <p className="mt-1.5 whitespace-pre-wrap text-[14px] leading-relaxed">
                      {incident.resolution}
                    </p>
                    {/* On the positive tint, where `ink-subtle` is 4.47:1. */}
                    {incident.resolvedAt ? (
                      <p className="mt-1.5 text-[12px] text-ink-2">
                        {when(incident.resolvedAt)}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="border-t border-light px-5 py-3">
                    {acting?.id === incident.id ? (
                      <div className="flex flex-col gap-2">
                        <textarea
                          value={text}
                          onChange={(event) => setText(event.target.value)}
                          rows={acting.kind === 'reply' ? 2 : 3}
                          aria-label={
                            acting.kind === 'resolve'
                              ? 'How it was settled'
                              : acting.kind === 'withdraw'
                                ? 'Why you are taking it back'
                                : 'Your reply'
                          }
                          placeholder={
                            acting.kind === 'resolve'
                              ? 'What happened, in words the director can act on.'
                              : acting.kind === 'withdraw'
                                ? 'They worked it out themselves.'
                                : ''
                          }
                          className="w-full rounded-md border border-stroke bg-chalk px-3 py-2 text-[14px] leading-relaxed"
                        />
                        <div className="flex items-center gap-2">
                          <Button
                            variant={acting.kind === 'withdraw' ? 'destructive' : 'primary'}
                            size="inline"
                            disabled={
                              (acting.kind !== 'withdraw' && text.trim() === '') ||
                              reply.isPending ||
                              resolve.isPending ||
                              withdraw.isPending
                            }
                            onClick={() => {
                              const payload = { id: incident.id, value: text };
                              if (acting.kind === 'reply') reply.mutate(payload);
                              else if (acting.kind === 'resolve') resolve.mutate(payload);
                              else withdraw.mutate(payload);
                            }}
                          >
                            {acting.kind === 'reply'
                              ? 'Reply'
                              : acting.kind === 'resolve'
                                ? 'Mark it settled'
                                : 'Take it back'}
                          </Button>
                          <Button
                            variant="secondary"
                            size="inline"
                            onClick={() => {
                              setActing(null);
                              setText('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant="secondary"
                          size="inline"
                          onClick={() => {
                            setActing({ id: incident.id, kind: 'reply' });
                            setText('');
                          }}
                        >
                          Reply
                        </Button>

                        {/* Settling is HQ's. She raised it because she could not settle it. */}
                        {isCommissioner ? (
                          <Button
                            variant="secondary"
                            size="inline"
                            onClick={() => {
                              setActing({ id: incident.id, kind: 'resolve' });
                              setText('');
                            }}
                          >
                            Settle it
                          </Button>
                        ) : (
                          <Button
                            variant="destructive"
                            size="inline"
                            onClick={() => {
                              setActing({ id: incident.id, kind: 'withdraw' });
                              setText('');
                            }}
                          >
                            Take it back
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function Message({
  who,
  body,
  at,
  fromHq,
}: {
  who: string;
  body: string;
  at: string;
  fromHq: boolean;
}) {
  return (
    /*
      THE SMALL TEXT STEPS UP ON THE TINTED ROW. `ink-subtle` is 4.45:1 on `tint-neutral` — it
      clears the bar on the page, on a card and on an inset panel, and misses on this fourth
      ground by five hundredths. Caught by an axe pass; see the recorded pairing in the contrast
      test, which exists so nobody puts it back.
    */
    <div className={['px-5 py-3.5', fromHq ? 'bg-tint-deep' : ''].join(' ')}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[13px] font-medium">
          {who}
          {fromHq ? (
            <span className={['ml-1.5 text-[12px]', fromHq ? 'text-ink-2' : 'text-ink-3'].join(' ')}>
              HQ
            </span>
          ) : null}
        </span>
        <span className={['text-[12px]', fromHq ? 'text-ink-2' : 'text-ink-3'].join(' ')}>
          {when(at)}
        </span>
      </div>
      <p className="mt-1 whitespace-pre-wrap text-[14px] leading-relaxed">{body}</p>
    </div>
  );
}
